import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Button,
  Alert,
  Snackbar
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getRefundRequestColumns } from './RefundRequestColumns';
import RefundRequestDetailsDialog from './RefundRequestDetailsDialog';
import RefundRequestApprovalDialog from './RefundRequestApprovalDialog';
import RefundRequestRejectDialog from './RefundRequestRejectDialog';
import { apiGet, apiRequest } from '../../../utils/fetch';
import authStore from '../../../store/authStore';

interface RefundRequestAdminListProps {
  statusFilter?: string; // For "Pending" tab
}

const RefundRequestAdminList: React.FC<RefundRequestAdminListProps> = ({ statusFilter }) => {
  const [requests, setRequests] = useState<any[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [requestIdFilter, setRequestIdFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [paymentIdFilter, setPaymentIdFilter] = useState('');
  const [statusFilterLocal, setStatusFilterLocal] = useState('all');
  const [itemTypeFilter, setItemTypeFilter] = useState('all');

  // Dialogs
  const [detailsDialog, setDetailsDialog] = useState<{ open: boolean; request: any }>({
    open: false,
    request: null
  });
  const [approvalDialog, setApprovalDialog] = useState<{ open: boolean; request: any }>({
    open: false,
    request: null
  });
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; request: any }>({
    open: false,
    request: null
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });


  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchRequests();
  }, []);


  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    applyFilters();
  }, [requests, requestIdFilter, userIdFilter, paymentIdFilter, statusFilterLocal, itemTypeFilter, statusFilter]);

  const fetchRequests = async () => {
    setLoading(true);
    setError(null);

    try {
      const endpoint = statusFilter
        ? `/refundRequests/status/${statusFilter}`
        : `/refundRequests`;

      const result = await apiGet<any>(endpoint);

      if (result.status && result.data) {
        setRequests(result.data || []);
      } else {
        setError(result.message || 'Failed to fetch refund requests');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...requests];

    // Request ID filter
    if (requestIdFilter.trim()) {
      const id = parseInt(requestIdFilter);
      if (!isNaN(id)) {
        filtered = filtered.filter((r) => r.id === id);
      }
    }

    // User ID filter
    if (userIdFilter.trim()) {
      const userId = parseInt(userIdFilter);
      if (!isNaN(userId)) {
        filtered = filtered.filter((r) => r.userId === userId);
      }
    }

    // Payment ID filter
    if (paymentIdFilter.trim()) {
      const paymentId = parseInt(paymentIdFilter);
      if (!isNaN(paymentId)) {
        filtered = filtered.filter((r) => r.paymentId === paymentId);
      }
    }

    // Status filter (local, not from tab)
    if (statusFilterLocal !== 'all') {
      filtered = filtered.filter((r) => r.status === statusFilterLocal);
    }

    // Item type filter
    if (itemTypeFilter !== 'all') {
      filtered = filtered.filter((r) => r.itemType === itemTypeFilter);
    }

    setFilteredRequests(filtered);
  };

  const handleViewDetails = (id: number) => {
    const request = requests.find((r) => r.id === id);
    if (request) {
      setDetailsDialog({ open: true, request });
    }
  };

  const handleApprove = (id: number) => {
    const request = requests.find((r) => r.id === id);
    if (request) {
      setApprovalDialog({ open: true, request });
    }
  };

  const handleReject = (id: number) => {
    const request = requests.find((r) => r.id === id);
    if (request) {
      setRejectDialog({ open: true, request });
    }
  };

  const handleApproveConfirm = async (requestId: number, reviewNotes: string) => {
    try {
      const result = await apiRequest<any>(
        `/refundRequests/approve/${requestId}`,
        'POST',
        {
          reviewNotes,
          userSessionData: authStore.userSessionData
        }
      );

      if (result.status) {
        setSnackbar({
          open: true,
          message: 'Refund request approved and processed successfully!',
          severity: 'success'
        });
        fetchRequests(); // Refresh list
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to approve refund request',
          severity: 'error'
        });
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'An error occurred',
        severity: 'error'
      });
    }
  };

  const handleRejectConfirm = async (requestId: number, reviewNotes: string) => {
    try {
      const result = await apiRequest<any>(
        `/refundRequests/reject/${requestId}`,
        'POST',
        {
          reviewNotes,
          userSessionData: authStore.userSessionData
        }
      );

      if (result.status) {
        setSnackbar({
          open: true,
          message: 'Refund request rejected successfully',
          severity: 'success'
        });
        fetchRequests(); // Refresh list
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to reject refund request',
          severity: 'error'
        });
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'An error occurred',
        severity: 'error'
      });
    }
  };

  const columns = getRefundRequestColumns(handleViewDetails, handleApprove, handleReject);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">
          {statusFilter ? `${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Refund Requests` : 'All Refund Requests'}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchRequests}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      {!statusFilter && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Request ID"
            variant="outlined"
            size="small"
            value={requestIdFilter}
            onChange={(e) => setRequestIdFilter(e.target.value)}
            sx={{ minWidth: 120 }}
          />
          <TextField
            label="User ID"
            variant="outlined"
            size="small"
            value={userIdFilter}
            onChange={(e) => setUserIdFilter(e.target.value)}
            sx={{ minWidth: 120 }}
          />
          <TextField
            label="Payment ID"
            variant="outlined"
            size="small"
            value={paymentIdFilter}
            onChange={(e) => setPaymentIdFilter(e.target.value)}
            sx={{ minWidth: 120 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilterLocal}
              label="Status"
              onChange={(e) => setStatusFilterLocal(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="processed">Processed</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Item Type</InputLabel>
            <Select
              value={itemTypeFilter}
              label="Item Type"
              onChange={(e) => setItemTypeFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="adSpace">Ad Space</MenuItem>
              <MenuItem value="tokenPlan">Token Plan</MenuItem>
              <MenuItem value="subscriptionPlan">Subscription Plan</MenuItem>
              <MenuItem value="purchasePlan">Purchase Plan</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {/* DataGrid */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredRequests}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } }
          }}
          disableRowSelectionOnClick
        />
      </Box>

      {/* Dialogs */}
      <RefundRequestDetailsDialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false, request: null })}
        request={detailsDialog.request}
      />

      <RefundRequestApprovalDialog
        open={approvalDialog.open}
        onClose={() => setApprovalDialog({ open: false, request: null })}
        request={approvalDialog.request}
        onApprove={handleApproveConfirm}
      />

      <RefundRequestRejectDialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog({ open: false, request: null })}
        request={rejectDialog.request}
        onReject={handleRejectConfirm}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RefundRequestAdminList;
